/**
 * Currency utilities for consistent currency formatting across the application
 */

// Currency configuration
export const CURRENCY = {
  CODE: 'XCG',
  SYMBOL: 'xcg',
  NAME: 'Caribbean Guilder',
  DECIMAL_PLACES: 2,
  DECIMAL_SEPARATOR: '.',
  THOUSANDS_SEPARATOR: ',',
};

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number | string,
  options: {
    showCode?: boolean;
    showSymbol?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string {
  const {
    showCode = false,
    showSymbol = true,
    minimumFractionDigits = CURRENCY.DECIMAL_PLACES,
    maximumFractionDigits = CURRENCY.DECIMAL_PLACES,
  } = options;

  // Convert string to number if needed
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Format the number with proper decimal places
  const formattedNumber = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  // Build the currency string
  let result = '';
  if (showSymbol) result += CURRENCY.SYMBOL + ' ';
  result += formattedNumber;
  if (showCode) result += ` ${CURRENCY.CODE}`;

  return result;
}

/**
 * Parse a currency string to a number
 * @param currencyString - The currency string to parse
 * @returns The parsed number
 */
export function parseCurrency(currencyString: string): number {
  // Remove currency symbol, code, and non-numeric characters except decimal point
  const cleanedString = currencyString
    .replace(CURRENCY.SYMBOL, '')
    .replace(CURRENCY.CODE, '')
    .replace(/[^\d.-]/g, '');
  
  return parseFloat(cleanedString);
}

/**
 * Convert USD to XCG
 * @param usdAmount - Amount in USD
 * @param exchangeRate - Exchange rate (USD to XCG)
 * @returns Amount in XCG
 */
export function convertUSDtoXCG(usdAmount: number, exchangeRate: number = 1.79): number {
  return usdAmount * exchangeRate;
}

/**
 * Convert XCG to USD
 * @param xcgAmount - Amount in XCG
 * @param exchangeRate - Exchange rate (XCG to USD)
 * @returns Amount in USD
 */
export function convertXCGtoUSD(xcgAmount: number, exchangeRate: number = 0.56): number {
  return xcgAmount * exchangeRate;
}